import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";

/**
 * Searchable category picker. Type to filter by label or macro (strict word-substring match).
 * If the typed text has no exact label match, always offers to request it from the admin.
 * Props: value, options [{id,label,macro,provisional}], onChange(id), onRequest(name)->Promise<cat|null>
 */
export default function CategoryCombobox({ value, options = [], onChange, onRequest }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [requesting, setRequesting] = useState(false);
  const selected = options.find((o) => o.id === value);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return options;
    const words = q.split(/\s+/).filter(Boolean);
    return options.filter((o) => {
      const hay = `${o.label} ${o.macro || ""}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [q, options]);

  const hasExact = useMemo(
    () => options.some((o) => (o.label || "").trim().toLowerCase() === q),
    [q, options]
  );
  const canRequest = q.length >= 2 && !hasExact && !!onRequest;

  const doRequest = async () => {
    const name = query.trim();
    if (name.length < 2 || !onRequest) return;
    setRequesting(true);
    try {
      const cat = await onRequest(name);
      if (cat) { onChange(cat.id); setOpen(false); setQuery(""); }
    } finally { setRequesting(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid="offer-category"
          className="h-11 w-full justify-between rounded-xl font-normal">
          <span className={`truncate flex items-center gap-1.5 ${selected ? "text-slate-900" : "text-slate-400"}`}>
            {selected ? selected.label : "Cerca o scegli categoria…"}
            {selected?.provisional && <span className="text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">in attesa</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 rounded-xl w-[var(--radix-popover-trigger-width)]">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Scrivi per cercare…" value={query} onValueChange={setQuery} data-testid="offer-category-search" />
          <CommandList className="max-h-64">
            {filtered.length === 0 && !canRequest && <CommandEmpty>Nessuna categoria trovata.</CommandEmpty>}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o.id} value={o.id}
                    onSelect={() => { onChange(o.id); setOpen(false); setQuery(""); }}
                    data-testid={`offer-category-opt-${o.id}`}>
                    <Check className={`mr-2 h-4 w-4 ${value === o.id ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.macro && <span className="text-xs text-slate-400 ml-2 shrink-0">{o.macro}</span>}
                    {o.provisional && <span className="ml-1.5 text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">in attesa</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {canRequest && (
              <div className="border-t p-2">
                <Button type="button" size="sm" onClick={doRequest} disabled={requesting}
                  data-testid="offer-category-request"
                  className="w-full rounded-lg bg-[#E63946] hover:bg-[#d62f3c] text-white justify-start">
                  {requesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Richiedi «{query.trim()}» all'admin
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
